import React, {useMemo, useRef} from 'react';
import {classes, style} from './doppe-action-type-videos-list-widget.st.css';
import {listViewCreateDataSource, useObservableValue} from '@wix/devzai-utils-react';
import {DoppeActionTypeVideosList} from '../doppe-action-type-videos-list';
import {
    DoppeActionType,
    useIsWidgetRenderedInPage,
    useWidgetRenderingContext
} from '../../../client-server-common/doppe-action-types/doppe-action-type';
import {
    DoppeActionTypeWidgetItemList,
    onListItemClick
} from '../../doppe-actions-common/doppe-action-type-widget-item-list/doppe-action-type-widget-item-list';
import {
    DoppeActionTypeListViewItem
} from '../../doppe-actions-common/doppe-action-type-list-view-item/doppe-action-type-list-view-item';
import {
    doppeHideableValueGetVisibleValue,
    doppeHideableValueIsVisibleAndNotEqualValue
} from '../../../client-server-common/types/doppe-hideable-value';
import {
    DoppeActionTypeVideoBindableWidget
} from '../../doppe-action-type-video/doppe-action-type-video-widget/doppe-action-type-video-widget';
import {
    DoppeActionListItemClickBehaviour,
    doppeBusinessEntityDtoIsAvailable,
    doppeExternalVideoProviderResolveBusinessEntityReference
} from '../../../client-server-common';
import {bymoPageContextPropsResolveBusinessEntity, useBymoPageContextProps} from '../../../doppe-sdk';
import {evaluateFunction, iterableMapToArray, ObservableValue} from '@wix/devzai-utils-common';
import {DoppeActionTypeVideoIcon} from '../doppe-action-type-videos-list-icons';
import {DoppeViewerActionContext} from '../../../components/doppe-viewer-action-context/doppe-viewer-action-context';
import {
    DoppeActionTypeWidgetLayout
} from '../../../components/doppe-action-type-widget-layout/doppe-action-type-widget-layout';
import {
    useDoppeViewerLangDictionary
} from '../../../components/doppe-viewer-lang-dictionary/doppe-viewer-lang-dictionary';
import {
    DoppeActionUnavailablePreview,
    DoppeActionUnavailableReason
} from '../../../components/doppe-action-unavailable-preview/doppe-action-unavailable-preview';

export const DoppeActionTypeVideosListWidget = React.memo(function DoppeActionTypeVideosListWidget(
    props: DoppeActionType.WidgetProps<DoppeActionTypeVideosList.ActionSettings>
) {
    const {
        actionContext,

        className,
        ...htmlAttributes
    } = props;

    const {
        renderingInnerStateController,
        observableCurrentItemIdToRender = new ObservableValue<string | null>(null)
    } = useWidgetRenderingContext();

    const actionSettings = actionContext.action.settings;

    const {
        videos,
        layout,
        itemClickBehaviour,
        showButton,
        itemShowDescription,
        itemShowImage,
        itemShowTitle,
    } = actionSettings;

    const expandedVideoId = useObservableValue(observableCurrentItemIdToRender);
    
    const currentVideoToShow = useMemo(
        () => videos.find(video => video.id === expandedVideoId) ?? null,
        [videos, expandedVideoId]);

    const bymoPageContextProps = useBymoPageContextProps();

    const existingVideos = useMemo(() => {
        return iterableMapToArray(videos, (video, skip) => {

            const {
                videoProviderId,
                videoProvider
            } = video;

            if (videoProvider !== null && videoProviderId !== null) {
                const boundEvent = bymoPageContextPropsResolveBusinessEntity(
                    bymoPageContextProps,
                    doppeExternalVideoProviderResolveBusinessEntityReference(videoProvider, videoProviderId)
                );

                if (doppeBusinessEntityDtoIsAvailable(boundEvent)) {
                    return {
                        ...video,
                        businessEntityDto: boundEvent
                    }
                } else {
                    return skip;
                }
            } else {
                return {
                    ...video,
                    businessEntityDto: undefined
                }
            }
        })
    }, [videos, bymoPageContextProps]);

    const videosListViewDataSource = useMemo(() => {
        return listViewCreateDataSource(existingVideos, {
            idFunction: item => item.id
        });
    }, [existingVideos]);


    const rootElementRef = useRef(null);

    const viewerLang = useDoppeViewerLangDictionary();

    const isRenderedInPage = useIsWidgetRenderedInPage();

    return (
        <DoppeActionTypeWidgetLayout
            {...htmlAttributes}
            className={style(classes.root, {}, className)}
            actionContext={actionContext}
            ref={rootElementRef}
        >
            {evaluateFunction(() => {
                if (!currentVideoToShow) {
                    if (videosListViewDataSource.length === 0) {
                        return (
                            <DoppeActionUnavailablePreview
                                reason={DoppeActionUnavailableReason.ContentUnavailable}
                            />
                        )
                    } else {
                        return (
                            <DoppeActionTypeWidgetItemList
                                className={style(classes.listContent)}
                                actionContext={actionContext}
                                layout={layout}
                                hasSeeMoreButton={showButton}
                                listTitle={actionSettings.mainTitle}
                                media={doppeHideableValueGetVisibleValue(actionSettings.mainImage, undefined)}
                                onItemClick={eventData => {
                                    onListItemClick({
                                        itemClickBehaviour: !isRenderedInPage && itemClickBehaviour === DoppeActionListItemClickBehaviour.PreviewItemAsPopup ? DoppeActionListItemClickBehaviour.PreviewItem : itemClickBehaviour,
                                        renderingInnerStateController,
                                        observableCurrentItemIdToRender,
                                        rootElementRef,
                                        actionContext: actionContext as DoppeViewerActionContext<any>,
                                        itemId: eventData.id,
                                        itemIndex: videos.findIndex(item => item.id === eventData.id),
                                        itemLink: eventData.externalVideoUrl,
                                        viewerLang: viewerLang,
                                    })
                                }}
                                renderItem={(dataItem, layout, index) => {
                                    const {
                                        videoImage,
                                        title,
                                        description,
                                        // businessEntityDto,
                                    } = dataItem;


                                    return (
                                        <DoppeActionTypeListViewItem
                                            actionContext={actionContext as DoppeViewerActionContext<any>}
                                            innerItemId={dataItem.id}
                                            innerItemIndex={index}
                                            title={title}
                                            description={doppeHideableValueIsVisibleAndNotEqualValue(description, '') ? description : ''}
                                            image={videoImage ?? null}
                                            layout={layout}
                                            gridImageRatio={16 / 9}
                                            centeredIcon={DoppeActionTypeVideoIcon}
                                            price={''}
                                            isOneItem={videosListViewDataSource.length === 1}
                                            isLink={itemClickBehaviour === DoppeActionListItemClickBehaviour.OpenLink}
                                            hideImageWhenPossible={!itemShowImage}
                                            hideTitleWhenPossible={!itemShowTitle}
                                            hideDescriptionWhenPossible={!itemShowDescription}
                                        />
                                    )
                                }}
                                dataSourceItems={videosListViewDataSource}
                            />
                        )
                    }
                }
                else {
                    return (
                        <DoppeActionTypeVideoBindableWidget
                            actionSettings={{
                                externalVideoUrl: currentVideoToShow.externalVideoUrl,
                                description: currentVideoToShow.description,
                                title: currentVideoToShow.title,
                                videoButtonText: currentVideoToShow.videoButtonText,
                                videoPlayerSettings: currentVideoToShow.videoPlayerSettings,
                                videoImage: currentVideoToShow.videoImage,
                                videoProvider: currentVideoToShow.videoProvider,
                                videoProviderId: currentVideoToShow.videoProviderId,
                            }}
                            reportGoalAchieved={() => {
                                actionContext.reportGoalAchieved(
                                    currentVideoToShow?.id,
                                    videos.findIndex(item => item.id === currentVideoToShow?.id)
                                );
                            }}
                            actionId={actionContext.action.id}
                            actionName={actionContext.action.name}
                        />
                    )
                }
            })}
        </DoppeActionTypeWidgetLayout>
    );
});

